import { useState, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useQueryColumns } from '@hooks/useQuery'
import { isGeoJsonColumn } from 'src/utils/chartTransformers'
import SortableContextWrapper from '@components/dnd-shared/SortableContextWrapper'
import EmptyChartState from '@components/GridLayout/WidgetView/EmptyChartState/EmptyChartState'
import type { ChartType } from 'src/types/dashboard'

interface AxisMappingProps {
  query: string
  chartType?: ChartType
  xAxis: string[]
  yAxis: string[]
  onChangeXAxis: (next: string[]) => void
  onChangeYAxis: (next: string[]) => void
}

export default function AxisMapping({
  query,
  chartType,
  xAxis,
  yAxis,
  onChangeXAxis,
  onChangeYAxis
}: AxisMappingProps) {
  const intl = useIntl()
  const { columns, colSpec } = useQueryColumns(query)
  const [search, setSearch] = useState('')

  const isSingleY = chartType === 'pie-chart' || chartType === 'map-chart'

  const numericColumns = useMemo(
    () =>
      new Set(
        (colSpec ?? []).filter((c) => c.uiType === 'number').map((c) => c.name)
      ),
    [colSpec]
  )

  const geoColumnNames = useMemo(
    () => new Set((colSpec ?? []).filter(isGeoJsonColumn).map((c) => c.name)),
    [colSpec]
  )

  const canBeY = (col: string) => {
    if (chartType === 'map-chart') return true
    return numericColumns.has(col)
  }

  const availableColumns = useMemo(() => {
    const term = search.trim().toLowerCase()
    return columns?.filter((col) => {
      const isAvailable = !xAxis.includes(col) && !yAxis.includes(col)
      const matchesSearch = term === '' || col.toLowerCase().includes(term)
      return isAvailable && matchesSearch && !geoColumnNames.has(col)
    })
  }, [columns, xAxis, yAxis, search, geoColumnNames])

  const handleToggleX = (col: string) => {
    if (xAxis.includes(col)) {
      onChangeXAxis(xAxis.filter((c) => c !== col))
      return
    }
    onChangeXAxis([col])
    if (yAxis.includes(col)) {
      onChangeYAxis(yAxis.filter((c) => c !== col))
    }
  }

  const handleToggleY = (col: string) => {
    if (yAxis.includes(col)) {
      onChangeYAxis(yAxis.filter((c) => c !== col))
      return
    }
    if (!canBeY(col)) return

    if (isSingleY) {
      onChangeYAxis([col])
    } else {
      onChangeYAxis([...yAxis, col])
    }

    if (xAxis.includes(col)) {
      onChangeXAxis(xAxis.filter((c) => c !== col))
    }
  }

  if (!columns?.length) {
    return (
      <>
        <EmptyChartState />
        <p className="axis-mapping-empty">
          <FormattedMessage id="item-editor.chart.axis.no-columns" />
        </p>
      </>
    )
  }

  const xLabelId =
    chartType === 'map-chart'
      ? 'item-editor.chart.axis.region'
      : 'item-editor.chart.axis.x'

  const yLabelId =
    chartType === 'map-chart'
      ? 'item-editor.chart.axis.metric'
      : 'item-editor.chart.axis.y'

  return (
    <fieldset className="axis-mapping">
      <legend>
        <span>
          <FormattedMessage id="item-editor.chart.axis.title" />
        </span>
        <strong>
          <span>X</span>
          <span>Y</span>
        </strong>
      </legend>

      <AxisBlock
        labelId={xLabelId}
        axisKey="x"
        items={xAxis}
        onReorder={onChangeXAxis}
        xAxis={xAxis}
        yAxis={yAxis}
        canBeY={canBeY}
        onToggleX={handleToggleX}
        onToggleY={handleToggleY}
      />

      <AxisBlock
        labelId={yLabelId}
        axisKey="y"
        items={yAxis}
        onReorder={onChangeYAxis}
        xAxis={xAxis}
        yAxis={yAxis}
        canBeY={canBeY}
        onToggleX={handleToggleX}
        onToggleY={handleToggleY}
      />

      <section className="axis-mapping-block axis-mapping-available">
        {availableColumns?.length > 0 && (
          <header className="axis-mapping-block-header">
            <h5>
              <FormattedMessage id="item-editor.chart.axis.available" />
            </h5>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'item-editor.chart.axis.search.placeholder'
              })}
            />
          </header>
        )}
        <ul className="axis-mapping-list">
          {availableColumns?.map((col) => (
            <AxisRow
              key={col}
              column={col}
              xChecked={false}
              yChecked={false}
              yDisabled={!canBeY(col)}
              onToggleX={handleToggleX}
              onToggleY={handleToggleY}
            />
          ))}
        </ul>
      </section>
    </fieldset>
  )
}

interface AxisBlockProps {
  labelId: string
  axisKey: 'x' | 'y'
  items: string[]
  onReorder: (next: string[]) => void
  xAxis: string[]
  yAxis: string[]
  canBeY: (col: string) => boolean
  onToggleX: (col: string) => void
  onToggleY: (col: string) => void
}

function AxisBlock({
  labelId,
  axisKey,
  items,
  onReorder,
  xAxis,
  yAxis,
  canBeY,
  onToggleX,
  onToggleY
}: AxisBlockProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = items?.findIndex((c) => c === active.id)
    const newIndex = items?.findIndex((c) => c === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    onReorder(arrayMove(items, oldIndex, newIndex))
  }

  return (
    <section className={`axis-mapping-block axis-mapping-${axisKey}`}>
      {items?.length > 0 && (
        <header className="axis-mapping-block-header">
          <h5>
            <FormattedMessage id={labelId} />
          </h5>
        </header>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContextWrapper
          items={items}
          strategy={verticalListSortingStrategy}
        >
          <ul className="axis-mapping-list">
            {items.map((col) => (
              <SortableAxisRow
                key={col}
                column={col}
                xChecked={xAxis.includes(col)}
                yChecked={yAxis.includes(col)}
                yDisabled={!canBeY(col) && !yAxis.includes(col)}
                onToggleX={onToggleX}
                onToggleY={onToggleY}
              />
            ))}
          </ul>
        </SortableContextWrapper>
      </DndContext>
    </section>
  )
}

interface AxisRowProps {
  column: string
  xChecked: boolean
  yChecked: boolean
  yDisabled?: boolean
  onToggleX: (col: string) => void
  onToggleY: (col: string) => void
}

function AxisRow({
  column,
  xChecked,
  yChecked,
  yDisabled,
  onToggleX,
  onToggleY
}: AxisRowProps) {
  return (
    <li className="axis-mapping-row">
      <span className="ellipsis" title={column}>
        {column}
      </span>
      <div className="checkboxes">
        <label>
          <input
            type="checkbox"
            checked={xChecked}
            onChange={() => onToggleX(column)}
            aria-label={`X: ${column}`}
          />
          <span className="circle" />
        </label>
        <label>
          <input
            type="checkbox"
            checked={yChecked}
            disabled={yDisabled}
            onChange={() => onToggleY(column)}
            aria-label={`Y: ${column}`}
          />
          <span className="circle" />
        </label>
      </div>
    </li>
  )
}

function SortableAxisRow(props: AxisRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: props.column })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition: transition ?? undefined,
    opacity: isDragging ? 0.3 : 1,
    cursor: 'grab'
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="axis-mapping-row"
      {...attributes}
      {...listeners}
    >
      <span className="ellipsis" title={props.column}>
        {props.column}
      </span>
      <div className="checkboxes">
        <label>
          <input
            type="checkbox"
            checked={props.xChecked}
            onChange={() => props.onToggleX(props.column)}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label={`X: ${props.column}`}
          />
          <span className="circle" />
        </label>
        <label>
          <input
            type="checkbox"
            checked={props.yChecked}
            disabled={props.yDisabled}
            onChange={() => props.onToggleY(props.column)}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label={`Y: ${props.column}`}
          />
          <span className="circle" />
        </label>
      </div>
    </li>
  )
}
